import { Group, Mesh } from 'three';
import { Rng } from '../../core/rng';
import type { Streetscape } from '../../content/routes';
import { photosFor } from '../../assets/photoFacades';
import { MODULE } from './facadePainters';
import type { Placement } from '../props/propField';
import { planBuilding, type Plan } from './archetypes';
import { BuildingSlot, type BuildingLook, type SlotPlacement } from './building';
import { BlockBuilder } from './blockBuilder';
import { atlasMaterial, facadeAtlas } from './facadeAtlas';
import { moduleWidth, type FacadeKind } from './facadeLibrary';

const SLOTS = 3;
/** Share of plots that use a real photo frontage when the leg has photos. */
const PHOTO_SHARE = 0.75;
const TREE_GREENS = ['#3f5f2e', '#4a6b33', '#37552a', '#56733a', '#44612f'];
const UMBRELLA_COLORS = ['#d62828', '#1d4e89', '#fcbf49', '#2a9d8f', '#f77f00', '#ffffff', '#6a4c93'];

export interface SideProps {
  trees: Placement[];
  tanks: Placement[];
  umbrellas: Placement[];
}

/** One side of one 40 m tile: three plots of buildings plus the trees, tanks and stalls in front of them. */
export class StreetSide {
  readonly group = new Group();
  /** Photo frontages need their own textures, so they keep separate meshes. */
  private readonly photoSlots: BuildingSlot[] = [];
  /** All painted buildings on this side, merged into one mesh (one draw call). */
  private readonly blocks = new Mesh(undefined, atlasMaterial());

  /** side = 1 for the kerb side (right), -1 for across the median. */
  constructor(private readonly side: 1 | -1, private readonly frontX: number, private readonly sidewalkX: number) {
    for (let i = 0; i < SLOTS; i += 1) this.photoSlots.push(new BuildingSlot(this.group));
    this.blocks.castShadow = true;
    this.blocks.receiveShadow = true;
    this.group.add(this.blocks);
  }

  /**
   * seed: identifies this physical side of this stretch of road, so the return trip shows the same buildings.
   * gap: a stretch (tile-local metres) kept clear for a junction's crossing road.
   * reverse: on the way back the street runs the other way along the tile, so positions are mirrored.
   */
  dress(seed: number, length: number, scape: Streetscape, legId: string, gap: { from: number; to: number } | null = null, reverse = false): SideProps {
    const rng = new Rng(seed + 17);
    const along = (z: number): number => (reverse ? -length - z : z);
    const props: SideProps = { trees: [], tanks: [], umbrellas: [] };
    let cursor = 0;
    const builder = new BlockBuilder();
    // The gap is given along the driving direction; buildings are laid out along the street, which runs the other way on a return trip.
    const span = gap && reverse ? { from: length - gap.to, to: length - gap.from } : gap;
    for (const slot of this.photoSlots) {
      slot.hide();
      if (span && cursor < span.to && cursor + 4 > span.from) cursor = span.to;
      const limit = span && cursor < span.from ? span.from : length;
      const width = Math.min(limit - cursor, rng.range(10, 17));
      if (width < 4) {
        slot.hide();
        continue;
      }
      const plan = planBuilding(rng.weighted(scape.mix).kind, width - rng.range(0.4, 2), rng);
      const z = along(-(cursor + width / 2));
      const photos = plan.compound ? [] : photosFor(legId);
      if (photos.length > 0 && rng.next() < PHOTO_SHARE) {
        const photo = rng.pick(photos);
        const height = Math.min(24, Math.max(4, plan.look.width / photo.aspect));
        slot.dressPhoto(photo.texture, plan.look, height, { ...this.placement(plan, z), groundX: this.frontX + this.side * (plan.look.depth / 2), groundDepth: plan.look.depth });
        plan.look.floors = Math.max(0, Math.round((height - plan.look.groundHeight) / MODULE.height));
      } else {
        this.addBlocks(builder, plan.look, this.placement(plan, z));
      }
      this.addProps(plan, z, width, scape, rng, props);
      cursor += width;
    }
    this.blocks.geometry.dispose();
    const geometry = builder.build();
    this.blocks.visible = geometry !== null;
    if (geometry) this.blocks.geometry = geometry;
    this.addTrees(length, scape, rng, props, along);
    if (gap) props.trees = props.trees.filter((p) => -p.z < gap.from - 1 || -p.z > gap.to + 1);
    return props;
  }

  private addBlocks(builder: BlockBuilder, look: BuildingLook, at: SlotPlacement): void {
    const atlas = facadeAtlas();
    const roof = atlas.colour(look.roof);
    const cellFor = (kind: FacadeKind, variant: number) => atlas.cell(`${kind}:${variant}`);
    const repeat = (kind: FacadeKind, metres: number): number => Math.max(1, Math.round(metres / moduleWidth(kind)));
    const ground = cellFor(look.ground, look.groundVariant);
    builder.add({
      x: at.groundX, y: look.groundHeight / 2, z: at.z, sx: at.groundDepth, sy: look.groundHeight, sz: look.width,
      front: ground, side: ground, roof, frontX: repeat(look.ground, look.width), rows: 1, sideX: repeat(look.ground, at.groundDepth),
    });
    if (look.floors <= 0) return;
    const height = look.floors * MODULE.height;
    const upper = cellFor(look.upper, look.variant);
    const width = look.width - (at.upperInset ?? 0);
    builder.add({
      x: at.upperX, y: at.upperBase + height / 2, z: at.z, sx: look.depth, sy: height, sz: width,
      front: upper, side: upper, roof, frontX: repeat(look.upper, width), rows: look.floors, sideX: repeat(look.upper, look.depth),
    });
  }

  private placement(plan: Plan, z: number): SlotPlacement {
    const out = (d: number): number => this.frontX + this.side * d;
    const { look } = plan;
    if (plan.compound) {
      return { z, groundX: out(0.2), groundDepth: 0.4, upperX: out(plan.setback + look.depth / 2), upperBase: 0, upperInset: 3 };
    }
    const centre = out(plan.setback + look.depth / 2);
    return { z, groundX: centre, groundDepth: look.depth, upperX: centre, upperBase: look.groundHeight };
  }

  private addProps(plan: Plan, z: number, width: number, scape: Streetscape, rng: Rng, props: SideProps): void {
    const { look } = plan;
    const roof = plan.compound ? look.floors * MODULE.height : look.groundHeight + look.floors * MODULE.height;
    for (let i = 0; i < plan.tanks; i += 1) {
      const d = plan.setback + rng.range(1.5, look.depth - 1.5);
      props.tanks.push({ x: this.frontX + this.side * d, y: roof, z: z + rng.range(-width / 3, width / 3) });
    }
    if (plan.compound) {
      props.trees.push({ x: this.frontX + this.side * rng.range(2.5, 5), y: 0, z: z + rng.range(-3, 3), scale: rng.range(0.9, 1.4), color: rng.pick(TREE_GREENS) });
    }
    if (plan.market || rng.next() < scape.umbrellas / 12) {
      const count = plan.market ? 3 : 1;
      for (let i = 0; i < count; i += 1) {
        const x = this.sidewalkX + this.side * rng.range(-1.2, 0.6);
        props.umbrellas.push({ x, y: 0.2, z: z + rng.range(-width / 2.5, width / 2.5), rotY: rng.range(0, 6), color: rng.pick(UMBRELLA_COLORS) });
      }
    }
  }

  private addTrees(length: number, scape: Streetscape, rng: Rng, props: SideProps, along: (z: number) => number): void {
    for (let i = 0; i < scape.trees; i += 1) {
      if (rng.next() > 0.75) continue;
      props.trees.push({
        x: this.sidewalkX + this.side * rng.range(0.8, 1.6),
        y: 0.2,
        z: along(-rng.range(2, length - 2)),
        rotY: rng.range(0, 6),
        scale: rng.range(0.8, 1.25),
        color: rng.pick(TREE_GREENS),
      });
    }
  }
}
